import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiLoader, FiList, FiChevronRight } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import CompactEpisodeRow from './CompactEpisodeRow';
import PodcastModal from './PodcastModal';
import { fetchPodcastFeed } from '../utils/rssParser';
import { useAudio } from '../context/AudioContext';

const RSS_FEED_URL = "https://api.riverside.fm/hosting/lyTmVj6F.rss";

const EpisodeList = () => {
  const [allEpisodes, setAllEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { playEpisode, currentEpisode, isPlaying } = useAudio();

  useEffect(() => {
    const loadFeed = async () => {
      setLoading(true);
      const data = await fetchPodcastFeed(RSS_FEED_URL);
      setAllEpisodes(data);
      setLoading(false);
    };
    loadFeed();
  }, []);

  const handleEpisodeClick = (episode) => {
    // 1. Play the episode
    playEpisode(episode, allEpisodes);
    // 2. Open the modal (pop up)
    setIsModalOpen(true);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-brand-tan">
        <SafeIcon icon={FiLoader} className="w-6 h-6 animate-spin mb-2" />
        <p className="font-bold uppercase tracking-widest text-[9px]">Loading Feed...</p>
      </div>
    );
  }

  // Compact View: Only 3 items
  const widgetEpisodes = allEpisodes.slice(0, 3);

  return (
    <div className="max-w-xl mx-auto px-4 py-8">
      {/* Widget Header */}
      <div className="flex items-end justify-between mb-4 px-1">
        <div>
          <h2 className="text-xl font-bold text-brand-dark font-serif">Latest Episodes</h2>
          <p className="text-[10px] text-brand-green/60 uppercase tracking-widest font-bold mt-1">
             Fresh from the Studio
          </p>
        </div>
        <button 
           onClick={() => setIsModalOpen(true)}
           className="text-[10px] font-bold uppercase tracking-widest text-brand-tan hover:text-brand-dark transition-colors flex items-center gap-1"
        >
           All Episodes <SafeIcon icon={FiChevronRight} className="w-3 h-3" />
        </button>
      </div>

      {/* Compact List */}
      <div className="flex flex-col gap-3">
        <AnimatePresence mode="wait">
          {widgetEpisodes.map((episode, idx) => (
            <motion.div
              key={episode.guid || episode.audioUrl || idx} // Use robust key
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: idx * 0.1 }}
            >
              <CompactEpisodeRow 
                episode={episode} 
                onClick={() => handleEpisodeClick(episode)}
                isPlayingEpisode={currentEpisode?.audioUrl === episode.audioUrl && isPlaying}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      <PodcastModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        episodes={allEpisodes} 
      />
    </div>
  );
};

export default EpisodeList;